import { getSettingShiftCodeFontSize } from "@utils/fontSizeUtils";
import type * as type from "@vTypes";
import { useEffect, useRef, useState, useMemo } from "react";
import CustomModal from "@/components/modal/CustomModal";

function ConfTableRow({
  data,
  updateShift,
  deleteShift,
  currentShiftManage,
  saveShiftManage,
  shiftIdFlatMap,
  offSwapOffShiftGb,
  disableDrag = false,
  onlyTimeAndColorEditable = false,
}: {
  data: type.UpdateShift;
  updateShift: (value: type.UpdateShift) => void;
  deleteShift: (value: string) => void;
  currentShiftManage: type.ShiftManageObject[];
  saveShiftManage: (value: type.ShiftManageObject[]) => void;
  shiftIdFlatMap: string[];
  offSwapOffShiftGb?: string | null;
  disableDrag?: boolean;
  /** true면 시간·색상만 수정 (특수 행용) */
  onlyTimeAndColorEditable?: boolean;
}) {
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [isSlotListOpend, setIsSlotListOpend] = useState<boolean>(false);
  const [isTypeListOpend, setIsTypeListOpend] = useState<boolean>(false);
  const [isFocusColorPalette, setIsFocusColorPalette] =
    useState<boolean>(false);

  const [copyArray, setCopyArray] = useState<type.UpdateShift>(data);
  const [copyCurrentShiftManage, setCopyCurrentShiftManage] = useState<
    type.ShiftManageObject[]
  >([]);

  const [isDuplicateShiftId, setIsDuplicateShiftId] = useState<boolean>(false);
  const [isAlertModalOpen, setIsAlertModalOpen] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] =
    useState<string>("근무코드를 입력해주세요");

  const rowRef = useRef<HTMLDivElement | null>(null);

  const dutyArray = ["근무", "공가", "휴가"];
  const slotArray = ["-", "데이", "이브닝", "나이트", "고정근무"];

  function updateShiftSlot(shifts: string, slot: number) {
    setCopyCurrentShiftManage((prev) =>
      prev.map((item) => {
        const newCodes = item.codes.filter((code) => code !== shifts);
        if (item.shift_slot === slot && slot > 0) {
          newCodes.push(shifts);
        }
        return { ...item, codes: newCodes };
      })
    );
  }

  function deleteShiftSlot(shift_id: string) {
    const deleteArray = currentShiftManage.map((item) => ({
      ...item,
      codes: item.codes.filter((code) => code !== shift_id),
    }));
    saveShiftManage(deleteArray);
  }

  const isDefaultShift = data.default_shift;
  /** 기본코드(default_shift 있음)는 구분·타입 모두 변경 불가(텍스트만 표시) */
  const isDivisionFixed = !!isDefaultShift;
  const isTypeFixed = !!isDefaultShift;
  const isOffSwapTargetVisible =
    copyArray.type !== "근무" &&
    copyArray.default_shift !== "O" &&
    !(offSwapOffShiftGb && copyArray.shift_gb === offSwapOffShiftGb);

  const updatedModifedShifts = () => {
    const normalizedShift =
      copyArray.type !== "근무"
        ? {
            ...copyArray,
            shift_gb: null,
            start_time: null,
            end_time: null,
          }
        : copyArray;
    const toSave = isOffSwapTargetVisible
      ? normalizedShift
      : {
          ...normalizedShift,
          off_swap_target: false,
        };
    updateShift(toSave);
  };

  function handleClickOutside(event: MouseEvent) {
    if (!rowRef.current) return;
    const target = event.target as Node | null;

    if (target && !rowRef.current.contains(target)) {
      if (isFocusColorPalette) {
        setIsFocusColorPalette(false);
      } else if (isEditMode) {
        setIsTypeListOpend(false);
        setIsEditMode(false);
        setCopyCurrentShiftManage(currentShiftManage);
        setIsDuplicateShiftId(false);
        setCopyArray(data);
      }
    }
  }

  useEffect(() => {
    setCopyCurrentShiftManage(currentShiftManage);
  }, [currentShiftManage]);

  useEffect(() => {
    setIsLoaded(true);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [data, isFocusColorPalette, isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      setCopyArray(data);
    }
    // isEditMode 의존성 배열에서 제거
    // 저장 시 스위치가 깜빡이는 현상 해결을 위함
  }, [data]);

  // console.log("ConfTableRow 렌더 - shift_id:", data.shift_id,
  //   "start_time:", copyArray.start_time,
  //   "타입:", typeof copyArray.start_time);

  const slotIndex = useMemo(() => {
    const target = isEditMode ? copyArray : data;
    if ((target as any).shift_gb === "고정") return 4;
    return (
      copyCurrentShiftManage.find((item) => item.codes.includes(data.shift_id))
        ?.shift_slot ?? 0
    );
  }, [copyCurrentShiftManage, data.shift_id, copyArray, isEditMode, data]);

  const slotText = useMemo(() => {
    const target = isEditMode ? copyArray : data;
    if (target.default_shift === "주" || target.default_shift === "O") {
      return "오프";
    }
    const gb = (target as any).shift_gb;
    if (gb === "고정") return "고정근무";
    return gb || "-";
  }, [data, copyArray, isEditMode]);

  if (!isLoaded) {
    return <div>로딩중입니다</div>;
  }
  return (
    <div
      ref={rowRef}
      className={`conf_shiftCode__gridTemplate conf_shiftCode__row hover:bg-zinc-200/50 ${isEditMode ? "editMode" : ""}`}
    >
      {/* 드래그 버튼 (disableDrag 시 숨김) */}
      <div className="conf_shiftCode__td">
        {!disableDrag && (
          <div className="conf_button__handler">
            <span className="img_iconHandler"></span>
          </div>
        )}
      </div>

      {/* 근무코드 */}
      <div className="conf_shiftCode__td">
        <div
          className={`conf_shiftDisplay ${isDuplicateShiftId ? "conf_shiftDisplay--duplicate" : ""}`}
          style={{ backgroundColor: copyArray.color }}
        >
          {isEditMode && !onlyTimeAndColorEditable ? (
            <input
              type="text"
              className="conf_shiftCode"
              value={copyArray.shift_id}
              onChange={(e) => {
                const changeValue = e.currentTarget.value;
                if (data.shift_id !== changeValue) {
                  setIsDuplicateShiftId(shiftIdFlatMap.includes(changeValue));
                } else {
                  setIsDuplicateShiftId(false);
                }
                setCopyArray((prev: any) => ({
                  ...prev,
                  shift_id: changeValue,
                }));
              }}
            />
          ) : (
            <span
              className="font-pretendard font-bold whitespace-nowrap"
              style={{
                fontSize: getSettingShiftCodeFontSize(copyArray.shift_id),
              }}
            >
              {copyArray.shift_id}
            </span>
          )}
        </div>
      </div>

      {/* 이름 */}
      <div className="conf_shiftCode__td">
        {isEditMode && !onlyTimeAndColorEditable ? (
          <input
            type="text"
            className="conf_shiftName editMode"
            value={copyArray.name}
            onInput={(e) => {
              const changeValue = e.currentTarget.value;
              setCopyArray((prev: any) => ({ ...prev, name: changeValue }));
            }}
          />
        ) : (
          <div className="conf_shiftName">{copyArray.name}</div>
        )}
      </div>

      {/* 구분: default_shift 있는 기본코드는 변경 불가 */}
      <div className="conf_shiftCode__td">
        {isDivisionFixed ? (
          <div className="conf_selectType">{slotText}</div>
        ) : isEditMode && !onlyTimeAndColorEditable ? (
          <div className="conf_selectType_wrap">
            <button
              type="button"
              className="conf_selectType conf_selectType--button"
              disabled={copyArray.type !== "근무"}
              style={
                copyArray.type !== "근무"
                  ? { cursor: "not-allowed", opacity: 0.5 }
                  : {}
              }
              onClick={() => {
                setIsSlotListOpend((prev) => !prev);
              }}
            >
              {slotText}
            </button>
            {isSlotListOpend && (
              <div className="conf_selectType_list">
                {slotArray.map((type, index) => {
                  const isSelected = index === slotIndex;
                  return (
                    <button
                      key={index}
                      type="button"
                      className={`conf_selectType_item ${isSelected && "selected"}`}
                      onClick={() => {
                        if (index === 4) {
                          setCopyArray((prev: any) => ({
                            ...prev,
                            shift_gb: "고정",
                            show_in_preference: false,
                          }));
                          updateShiftSlot(data.shift_id, 0);
                        } else {
                          const newShiftGb =
                            index === 0 ? null : slotArray[index];
                          setCopyArray((prev: any) => ({
                            ...prev,
                            shift_gb: newShiftGb,
                            show_in_preference:
                              newShiftGb !== null
                                ? false
                                : prev.show_in_preference,
                          }));
                          updateShiftSlot(data.shift_id, index);
                        }
                        setIsSlotListOpend(false);
                      }}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="conf_selectType">{slotText}</div>
        )}
      </div>

      {/* 근무시간: 타입이 근무일 때만 time input, 그 외(공가/휴가)는 '-' div + null */}
      <div className="flex justify-center items-center">
        <div className="grid grid-cols-[1fr_auto_1fr] gap-x-1 flex-1 max-w-[220px] items-center">
          {copyArray.type !== "근무" ? (
            <div className=" text-center flex items-center justify-center">
              -
            </div>
          ) : (
            <input
              type="time"
              className={`conf_shift_time ${isEditMode ? "edit" : ""} ${!copyArray.start_time ? "empty" : ""}`}
              value={copyArray.start_time ?? ""}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setCopyArray((prev) => ({ ...prev, start_time: value }));
              }}
              disabled={!isEditMode}
            />
          )}
          ~
          {copyArray.type !== "근무" ? (
            <div className=" text-center flex items-center justify-center">
              -
            </div>
          ) : (
            <input
              type="time"
              className={`conf_shift_time ${isEditMode ? "edit" : ""} ${!copyArray.end_time ? "empty" : ""}`}
              value={copyArray.end_time ?? ""}
              onChange={(e) => {
                const value = e.currentTarget.value;
                setCopyArray((prev) => ({ ...prev, end_time: value }));
              }}
              disabled={!isEditMode}
            />
          )}
        </div>
      </div>

      {/* 타입: 기본코드(default_shift 있음)는 고정 */}
      <div className="conf_shiftCode__td">
        {isTypeFixed ? (
          <div className="conf_selectType">{copyArray.type}</div>
        ) : isEditMode && !onlyTimeAndColorEditable ? (
          <div className="conf_selectType_wrap">
            <button
              type="button"
              className="conf_selectType conf_selectType--button"
              onClick={() => {
                setIsTypeListOpend((prev) => !prev);
              }}
            >
              {copyArray.type}
            </button>
            {isTypeListOpend && (
              <div className="conf_selectType_list">
                {dutyArray.map((type, index) => {
                  const isSelected = copyArray.type === type;
                  return (
                    <button
                      key={index}
                      type="button"
                      className={`conf_selectType_item ${isSelected && "selected"}`}
                      onClick={() => {
                        setCopyArray((prev) => ({ ...prev, type: type }));
                        setIsTypeListOpend(false);
                        if (type !== "근무") {
                          setCopyArray((prev: any) => ({
                            ...prev,
                            shift_gb: null,
                            start_time: null,
                            end_time: null,
                          }));
                          updateShiftSlot(data.shift_id, 0);
                        } else {
                          setCopyArray((prev: any) => ({
                            ...prev,
                            off_swap_target: false,
                          }));
                        }
                      }}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="conf_selectType">{copyArray.type}</div>
        )}
      </div>

      {/* 원티드 반영: 구분(shift_gb) null인 것만 on/off, 그 외 disabled */}
      <div className="conf_shiftCode__td">
        {isOffSwapTargetVisible && (
          <label
            className={`conf_slideSwitch ${!isEditMode ? "conf_slideSwitch--disabled" : ""}`}
            title={
              isEditMode
                ? "초과 OFF 대체 코드"
                : "수정 버튼을 누른 뒤 변경할 수 있습니다."
            }
          >
            <input
              type="checkbox"
              checked={Boolean(copyArray.off_swap_target)}
              disabled={!isEditMode}
              onChange={(e) => {
                if (!isEditMode) return;
                const checked = e.currentTarget.checked;
                setCopyArray((prev) => ({
                  ...prev,
                  off_swap_target: checked,
                }));
              }}
              aria-label={`${copyArray.shift_id} OFF 대체 코드로 지정`}
            />
            <span className="conf_switchHandler"></span>
          </label>
        )}
      </div>

      <div className="conf_shiftCode__td">
        {(() => {
          const 원티드반영비활성 = Boolean(
            !isEditMode ||
              onlyTimeAndColorEditable ||
              isDefaultShift ||
              (copyArray as any).shift_gb !== null
          );
          return (
            <label
              className={`conf_slideSwitch ${원티드반영비활성 ? "conf_slideSwitch--disabled" : ""}`}
              title={
                원티드반영비활성
                  ? "구분이 '-'인 근무코드만 원티드 반영 on/off 가능합니다"
                  : undefined
              }
            >
              <input
                type="checkbox"
                checked={(copyArray as any).show_in_preference ?? false}
                disabled={원티드반영비활성}
                onChange={(e) => {
                  if (원티드반영비활성) return;
                  const checked = e.currentTarget.checked;
                  setCopyArray((prev: any) => ({
                    ...prev,
                    show_in_preference: checked,
                  }));
                }}
              />
              <span className="conf_switchHandler"></span>
            </label>
          );
        })()}
      </div>

      {/* 색상 */}
      <div className="conf_shiftCode__td">
        <label
          className={`conf_shiftColor__label ${isEditMode ? "edit" : ""}`}
          style={{ backgroundColor: copyArray.color }}
        >
          <input
            type="color"
            disabled={!isEditMode}
            value={copyArray.color}
            onFocus={() => {
              setIsFocusColorPalette(true);
            }}
            onBlur={() => {
              setIsFocusColorPalette(false);
            }}
            onChange={(e) => {
              const changeValue = e.currentTarget.value;
              setCopyArray((prev) => ({ ...prev, color: changeValue }));
            }}
          />
        </label>
      </div>

      {/* 수정버튼 */}
      <div className="conf_shiftCode__td">
        {isEditMode ? (
          <button
            type="button"
            className={`conf_mode_saveButton ${isDuplicateShiftId ? "errorShiftId hoverMessageWrap" : ""}`}
            disabled={isDuplicateShiftId}
            onClick={() => {
              if (!copyArray.shift_id || copyArray.shift_id.trim() === "") {
                setAlertMessage("근무코드를 입력해주세요");
                setIsAlertModalOpen(true);
                return;
              }
              if (!copyArray.name || copyArray.name.trim() === "") {
                setAlertMessage("이름을 입력해주세요");
                setIsAlertModalOpen(true);
                return;
              }
              updatedModifedShifts();
              saveShiftManage(copyCurrentShiftManage);
              setIsTypeListOpend(false);
              setIsEditMode(false);
            }}
          >
            저장
            {isDuplicateShiftId && (
              <span className="hoverMessage">
                동일한 근무코드가 존재합니다.
              </span>
            )}
          </button>
        ) : (
          <button
            type="button"
            className="conf_mode_editButton"
            onClick={() => {
              setIsEditMode(true);
            }}
          ></button>
        )}
      </div>

      {/* 삭제버튼 */}
      <div className="conf_shiftCode__td">
        {isEditMode &&
          (isDefaultShift ? (
            <div className="conf_mode_delButton hoverMessageWrap">
              <div className="hoverMessage hoverMessage--right">
                기본 근무코드는 삭제가 불가능합니다
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="conf_mode_delButton"
              onClick={() => {
                deleteShift(data.shift_id);
                deleteShiftSlot(data.shift_id);
              }}
            ></button>
          ))}
      </div>
      <CustomModal.Alert
        isOpen={isAlertModalOpen}
        onClose={() => setIsAlertModalOpen(false)}
        title={alertMessage}
      />
    </div>
  );
}

export default ConfTableRow;
